import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import { CheckCircle, Cancel, Remove, ExpandMore } from '@mui/icons-material';
import { API_BASE_URL } from '../../lib/config';

interface TeamPerformanceProps {
  season: number;
}

interface GameResult {
  game_id: number;
  week: number;
  game_date: string;
  opponent_abbr: string;
  opponent_name: string;
  opponent_logo: string;
  user_pick: string;
  picked_team_abbr: string;
  result: string;
  home_score: number;
  away_score: number;
  home_spread: number;
}

interface TeamRecord {
  team_abbr: string;
  team_name: string;
  team_logo: string;
  division: string;
  conference: string;
  wins: number;
  losses: number;
  pushes: number;
  total: number;
  games: GameResult[];
}

interface UserTeamPerformance {
  user_id: string;
  display_name: string;
  team_records: TeamRecord[];
}

interface TeamGameDetailModalProps {
  open: boolean;
  onClose: () => void;
  teamData: TeamRecord;
  userName: string;
}

const TeamGameDetailModal: React.FC<TeamGameDetailModalProps> = ({
  open,
  onClose,
  teamData,
  userName
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={2}>
          <img
            src={teamData.team_logo}
            alt={teamData.team_name}
            style={{ width: 40, height: 40, objectFit: 'contain' }}
          />
          <Box>
            <Typography variant="h6">
              {userName}'s {teamData.team_name} Picks
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {teamData.wins}-{teamData.losses}
              {teamData.pushes > 0 && `-${teamData.pushes}`}
              {' '}({teamData.total > 0 ? ((teamData.wins / teamData.total) * 100).toFixed(0) : 0}%)
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Week</TableCell>
              <TableCell>Opponent</TableCell>
              <TableCell>Picked</TableCell>
              <TableCell>Spread</TableCell>
              <TableCell>Score</TableCell>
              <TableCell>Result</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {teamData.games.map((game: GameResult) => {
              const pickedThisTeam = game.picked_team_abbr === teamData.team_abbr;
              return (
                <TableRow
                  key={game.game_id}
                  sx={{
                    backgroundColor: game.result === 'win' ? '#e8f5e9' :
                                     game.result === 'loss' ? '#ffebee' : '#f5f5f5'
                  }}
                >
                  <TableCell>{game.week}</TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <img
                        src={game.opponent_logo}
                        alt={game.opponent_name}
                        style={{ width: 24, height: 24, objectFit: 'contain' }}
                      />
                      vs {game.opponent_abbr}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={pickedThisTeam ? teamData.team_abbr : game.opponent_abbr}
                      size="small"
                      color={pickedThisTeam ? 'primary' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    {game.home_spread > 0 ? '+' : ''}{game.home_spread}
                  </TableCell>
                  <TableCell>
                    {game.home_score} - {game.away_score}
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={game.result === 'win' ? <CheckCircle /> :
                            game.result === 'loss' ? <Cancel /> : <Remove />}
                      label={game.result.toUpperCase()}
                      color={game.result === 'win' ? 'success' :
                             game.result === 'loss' ? 'error' : 'default'}
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const TeamPerformance: React.FC<TeamPerformanceProps> = ({ season }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState<UserTeamPerformance[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<{
    teamData: TeamRecord;
    userName: string;
  } | null>(null);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [season]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await fetch(`${API_BASE_URL}/.netlify/functions/analysis/team-performance/${season}`);

      if (!response.ok) {
        throw new Error('Failed to fetch team performance data');
      }

      const result = await response.json();
      setData(result.users);
    } catch (err: any) {
      setError(err.message || 'Failed to load team performance data');
    } finally {
      setLoading(false);
    }
  };

  const getWinPercentageColor = (wins: number, total: number) => {
    if (total === 0) return '#f5f5f5';
    const percentage = (wins / total) * 100;

    if (percentage >= 65) return '#4CAF50'; // Green
    if (percentage >= 50) return '#FFC107'; // Yellow
    return '#f44336'; // Red
  };

  const getTextColor = (total: number) => {
    return total > 0 ? 'white' : '#999';
  };

  const groupByDivision = (teamRecords: TeamRecord[]) => {
    const divisions = new Map<string, TeamRecord[]>();

    teamRecords.forEach(team => {
      const div = team.division || 'Unknown';
      if (!divisions.has(div)) {
        divisions.set(div, []);
      }
      divisions.get(div)!.push(team);
    });

    // Sort teams within each division alphabetically
    divisions.forEach(teams => {
      teams.sort((a: TeamRecord, b: TeamRecord) => a.team_abbr.localeCompare(b.team_abbr));
    });

    return divisions;
  };

  const calculateDivisionStats = (teams: TeamRecord[]) => {
    const totalWins = teams.reduce((sum, team) => sum + team.wins, 0);
    const totalLosses = teams.reduce((sum, team) => sum + team.losses, 0);
    const totalPushes = teams.reduce((sum, team) => sum + team.pushes, 0);
    const totalGames = teams.reduce((sum, team) => sum + team.total, 0);

    return { wins: totalWins, losses: totalLosses, pushes: totalPushes, total: totalGames };
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (data.length === 0) {
    return (
      <Box p={3}>
        <Alert severity="info">No team performance data available for this season yet.</Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {data.map((user) => {
        const divisionGroups = groupByDivision(user.team_records);

        return (
          <Paper key={user.user_id} elevation={3} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              {user.display_name}'s Performance by Team
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Win rate against each NFL team (Green: ≥65%, Yellow: 50-65%, Red: &lt;50%). Click division to expand, then click any team to see game details.
            </Typography>

            {/* Render each division */}
            {['AFC East', 'AFC North', 'AFC South', 'AFC West',
              'NFC East', 'NFC North', 'NFC South', 'NFC West'].map(division => {
              const teams = divisionGroups.get(division) || [];
              if (teams.length === 0) return null;

              const divStats = calculateDivisionStats(teams);
              const divWinRate = divStats.total > 0 ? ((divStats.wins / divStats.total) * 100).toFixed(0) : '0';

              return (
                <Accordion key={division} sx={{ mb: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                      <Typography variant="subtitle1" fontWeight="bold" color="primary">
                        {division}
                      </Typography>
                      <Chip
                        label={`${divStats.wins}-${divStats.losses}${divStats.pushes > 0 ? `-${divStats.pushes}` : ''}`}
                        size="small"
                        color={divStats.total > 0 ? 'primary' : 'default'}
                      />
                      <Typography variant="caption" color="text.secondary">
                        ({divWinRate}% win rate)
                      </Typography>
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails>
                    <Box sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
                      gap: 1.5
                    }}>
                      {teams.map((team: TeamRecord) => {
                        const winRate = team.total > 0 ? ((team.wins / team.total) * 100).toFixed(0) : 'N/A';
                        const backgroundColor = getWinPercentageColor(team.wins, team.total);
                        const textColor = getTextColor(team.total);

                        return (
                          <Tooltip
                            key={team.team_abbr}
                            title={`Click to see all ${team.team_name} games`}
                            arrow
                          >
                            <Box
                              onClick={() => setSelectedTeam({ teamData: team, userName: user.display_name })}
                              sx={{
                                p: 2,
                                textAlign: 'center',
                                borderRadius: 2,
                                backgroundColor,
                                color: textColor,
                                cursor: 'pointer',
                                transition: 'transform 0.2s, box-shadow 0.2s',
                                minHeight: '120px',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center',
                                '&:hover': {
                                  transform: 'scale(1.05)',
                                  boxShadow: 3
                                }
                              }}
                            >
                              {/* Team Logo */}
                              <img
                                src={team.team_logo}
                                alt={team.team_name}
                                style={{
                                  width: 40,
                                  height: 40,
                                  marginBottom: 8,
                                  objectFit: 'contain'
                                }}
                              />

                              {/* Team Abbreviation */}
                              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 0.5 }}>
                                {team.team_abbr}
                              </Typography>

                              {/* Record */}
                              <Typography variant="body2" sx={{ mb: 0.5 }}>
                                {team.wins}-{team.losses}
                                {team.pushes > 0 && `-${team.pushes}`}
                              </Typography>

                              {/* Win Percentage */}
                              {team.total > 0 && (
                                <Typography variant="caption" fontWeight="medium">
                                  {winRate}%
                                </Typography>
                              )}
                            </Box>
                          </Tooltip>
                        );
                      })}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Paper>
        );
      })}

      {/* Game Detail Modal */}
      {selectedTeam && (
        <TeamGameDetailModal
          open={!!selectedTeam}
          onClose={() => setSelectedTeam(null)}
          teamData={selectedTeam.teamData}
          userName={selectedTeam.userName}
        />
      )}
    </Box>
  );
};

export default TeamPerformance;
